/**
 * Central client store.
 *
 * Deliberately mixed: holds BOTH Core session state (status, user,
 * autoLockMs) AND Vault-specific key state (enrolment, encryptedPrivateKey,
 * wrappedVaultKey, privateKey, vaultKey, rawVaultKey, the `lock()` zeroing
 * behaviour). This is a single-purpose Documentation deployment so the mix
 * is honest staging rather than temporary.
 *
 * Vault-specific cleanup on lock is done via `registerLockCallback(fn)` —
 * the vault module calls this once at load (see `apps/vault/index.tsx`).
 * Core fires every callback inside `lock()` before zeroing keys.
 */
import { create } from 'zustand';
import {
  AUTO_LOCK_DEFAULT_MS,
  type EncryptedPrivateKey,
  type VaultEnrolmentState,
  type VaultLoginState,
  type VaultRole,
  type WrappedVaultKey,
} from '@dragonhq/protocol';
import {
  generateKeypair,
  generateVaultKey,
  importVaultKey,
  openWrappedKey,
  sealToPublicKey,
  unlockPrivateKey,
} from '../crypto';
import { api, getAccessToken, refreshAccessToken, setAccessToken } from './api';

/**
 * Normalise the vault state off a login / enrolment response. Handles the
 * null-from-/auth/me/enrolment case (user row missing) by returning a safe
 * "not enrolled" shape so callers always receive a fully-populated object.
 */
function normaliseVaultState(state: VaultLoginState | null | undefined): VaultLoginState {
  if (!state) {
    return {
      enrolment: 'needs_keypair' as VaultEnrolmentState,
      wrappedKey: null as WrappedVaultKey | null,
      vaultExists: false,
      roleInVault: null as VaultRole | null,
    };
  }
  return state;
}

// Lock callbacks — the vault module registers once at module load; all fire
// when lock() runs. A plain module-level Set rather than a store field
// because callbacks are registrations, not state the UI reacts to.
const lockCallbacks = new Set<() => void>();

/**
 * Sign-out prep callback. Apps that have ANY durable client-side state
 * (queued mutations, pendingSync rows, in-memory Y.Docs not yet flushed)
 * register one of these so the sign-out flow can:
 *   1. Flush whatever can be flushed before keys are zeroed.
 *   2. Report how many edits could not be flushed — sign-out asks the user
 *      whether to discard them rather than silently losing data.
 *   3. Wipe the app's IndexedDB only AFTER the user confirms — protecting
 *      against the "stale ghost row survives sign-out" failure mode.
 */
export type SignOutPrepResult = {
  /** Edits that COULD NOT be flushed before sign-out. */
  pendingCount: number;
  /** Description shown in the confirm dialog ("3 unsynced edit(s)"). */
  description?: string;
  /** Run this after the user confirms; wipes the app's IndexedDB. */
  wipe: () => Promise<void>;
};
export type SignOutPrep = () => Promise<SignOutPrepResult>;
const signOutPrepCallbacks = new Set<SignOutPrep>();

/**
 * App-supplied "how many edits are stuck in IndexedDB waiting for sync?"
 * probe. Used by `IdentityUnlockScreen` to surface a banner when auto-lock
 * has fired with pending writes — without this, the tech sees a generic
 * unlock prompt and may close the tab without realising their morning's
 * work is sitting there.
 */
export type PendingCountProvider = () => Promise<number>;
const pendingCountProviders = new Set<PendingCountProvider>();

interface VaultState {
  /** Core-level session status. */
  status: 'hydrating' | 'signed_out' | 'authenticated';
  user: { id: string; username: string; email: string; role: string } | null;
  enrolment: VaultEnrolmentState | null;
  encryptedPrivateKey: EncryptedPrivateKey | null;
  wrappedVaultKey: WrappedVaultKey | null;
  vaultExists: boolean;
  /**
   * The caller's role inside the Documentation vault. Drives client-side
   * permission gating via `apps/vault/lib/usePermissions.ts`. Null when the
   * user isn't yet enrolled (enrolment ∈ {needs_keypair, needs_invitation}).
   * Mirrored from the login response's `vaultState.roleInVault` and
   * refreshed on every `hydrate()` / `refreshEnrolment()`.
   */
  vaultRole: VaultRole | null;
  /** Raw X25519 private key (32 bytes) cached in memory while unlocked. */
  privateKey: Uint8Array | null;
  /** Shared vault AES key, imported for use. */
  vaultKey: CryptoKey | null;
  /** Raw shared vault key bytes, used for sealing to other users during invites. */
  rawVaultKey: Uint8Array | null;

  autoLockMs: number;
  _lockTimer: ReturnType<typeof setTimeout> | null;

  afterLogin(args: {
    accessToken: string;
    user: { id: string; username: string; email: string; role: string };
    encryptedPrivateKey: EncryptedPrivateKey | null;
    vaultState: VaultLoginState;
  }): void;
  /** Restore session from persisted access token on page refresh. */
  hydrate(): Promise<void>;
  setupMasterCode(masterCode: string): Promise<void>;
  unlock(masterCode: string): Promise<void>;
  refreshEnrolment(): Promise<void>;
  sealForInvitee(publicKey: string): Promise<WrappedVaultKey>;
  lock(): void;
  signOut(): Promise<void>;
  touch(): void;
  setAutoLockMs(ms: number): void;
}

export const useVault = create<VaultState>((set, get) => ({
  status: 'hydrating',
  user: null,
  enrolment: null,
  encryptedPrivateKey: null,
  wrappedVaultKey: null,
  vaultExists: false,
  vaultRole: null,
  privateKey: null,
  vaultKey: null,
  rawVaultKey: null,
  autoLockMs: AUTO_LOCK_DEFAULT_MS,
  _lockTimer: null,

  afterLogin({ accessToken, user, encryptedPrivateKey, vaultState }) {
    setAccessToken(accessToken);
    const vault = normaliseVaultState(vaultState);
    set({
      user,
      enrolment: vault.enrolment,
      encryptedPrivateKey,
      wrappedVaultKey: vault.wrappedKey,
      vaultExists: vault.vaultExists,
      vaultRole: vault.roleInVault,
      status: 'authenticated',
      privateKey: null,
      vaultKey: null,
      rawVaultKey: null,
    });
  },

  async hydrate() {
    // Called once on app mount. If we have an access token, rebuild the session
    // from the server's view. Symmetric keys are NEVER persisted, so the user
    // has to re-enter their Master Code the first time they open a crypto-using
    // app post-refresh — but they don't have to redo password + TOTP.
    const token = localStorage.getItem('dragonhq.token');
    if (!token) {
      set({ status: 'signed_out' });
      return;
    }
    try {
      const st = await api.myEnrolment();
      if (!st.user) {
        setAccessToken(null);
        set({ status: 'signed_out' });
        return;
      }
      const vault = normaliseVaultState(st.vaultState);
      set({
        user: {
          id: st.user.id,
          username: st.user.username,
          email: st.user.email,
          role: st.user.role,
        },
        enrolment: vault.enrolment,
        encryptedPrivateKey: st.encryptedPrivateKey,
        wrappedVaultKey: vault.wrappedKey,
        vaultExists: vault.vaultExists,
        vaultRole: vault.roleInVault,
        status: 'authenticated',
        privateKey: null,
        vaultKey: null,
        rawVaultKey: null,
      });
    } catch {
      // Token expired or server rejected it; drop back to login.
      setAccessToken(null);
      set({ status: 'signed_out' });
    }
  },

  async setupMasterCode(masterCode: string) {
    // Defense in depth: if the store already has an encryptedPrivateKey,
    // the user is enrolled and should be on IdentityUnlockScreen — never
    // here. Generating a new keypair would discard their real one. The
    // server-side 409 on /auth/keypair is the load-bearing guard; this
    // catch surfaces a clearer error before the round-trip.
    if (get().encryptedPrivateKey) {
      throw new Error('Account already enrolled — refresh and unlock with your existing Master Code.');
    }
    // 1. Generate X25519 keypair, wrap private key with Master Code-derived KEK.
    const bundle = await generateKeypair(masterCode);
    await api.setKeypair({
      publicKey: bundle.publicKey,
      encryptedPrivateKey: bundle.encryptedPrivateKey,
    });

    const { vaultExists } = get();
    if (!vaultExists) {
      // First user ever: bootstrap the organisation vault now.
      const raw = generateVaultKey();
      const wrapped = await sealToPublicKey(raw, bundle.publicKey);
      await api.bootstrapVault(wrapped);
      const vk = await importVaultKey(raw);
      set({
        privateKey: bundle.privateKey,
        rawVaultKey: raw,
        vaultKey: vk,
        wrappedVaultKey: wrapped,
        encryptedPrivateKey: bundle.encryptedPrivateKey,
        enrolment: 'ready',
        vaultExists: true,
        // Bootstrapper is the sole `owner` on the server side — see
        // `services/vault.ts::bootstrapVault`. Mirror that locally so the
        // launcher shows admin affordances without a page refresh.
        vaultRole: 'owner',
      });
      get().touch();
    } else {
      // Not first user: they need an admin to invite them now.
      set({
        privateKey: bundle.privateKey,
        encryptedPrivateKey: bundle.encryptedPrivateKey,
        enrolment: 'needs_invitation',
      });
    }
  },

  async unlock(masterCode: string) {
    const { encryptedPrivateKey, wrappedVaultKey } = get();
    if (!encryptedPrivateKey) throw new Error('No keypair on record.');
    if (!wrappedVaultKey) throw new Error('You are not a vault member yet.');
    const priv = await unlockPrivateKey(encryptedPrivateKey, masterCode);
    const raw = await openWrappedKey(wrappedVaultKey, priv);
    const vk = await importVaultKey(raw);
    // Server-side reachability probe BEFORE we hand control to the app.
    // Master-Code unlock is purely client-side (decrypts the local KEK), so
    // without this a session whose JWT has expired or been revoked — typically
    // because the user moved networks and the previous session has lapsed —
    // would land in ghost-write territory: every outbox POST 401s, the
    // permanent-error path drops the ops, status flips green, and a later
    // "Resync from Server" wipes the IDB-only pages.
    //
    // refreshAccessToken returns false on (a) explicit 401 — token cleared —
    // or (b) network error — token preserved. Only (a) is a definite signal
    // that re-auth is required; on (b) we proceed optimistically so a flaky
    // cafe wifi doesn't block unlock.
    const refreshed = await refreshAccessToken();
    if (!refreshed && !getAccessToken()) {
      priv.fill(0);
      raw.fill(0);
      throw new Error(
        'Your session has expired. Click "Sign out" below and sign in again to continue.',
      );
    }
    set({ privateKey: priv, rawVaultKey: raw, vaultKey: vk });
    get().touch();
  },

  async refreshEnrolment() {
    const st = await api.myEnrolment();
    const vault = normaliseVaultState(st.vaultState);
    set({
      enrolment: vault.enrolment,
      wrappedVaultKey: vault.wrappedKey,
      vaultExists: vault.vaultExists,
      vaultRole: vault.roleInVault,
    });
  },

  async sealForInvitee(publicKey: string) {
    const raw = get().rawVaultKey;
    if (!raw) throw new Error('Vault is locked.');
    return sealToPublicKey(raw, publicKey);
  },

  lock() {
    const t = get()._lockTimer;
    if (t) clearTimeout(t);
    // Zero out the private key + raw vault key in memory.
    const { privateKey, rawVaultKey } = get();
    if (privateKey) privateKey.fill(0);
    if (rawVaultKey) rawVaultKey.fill(0);
    // Let each app clean up (e.g. vault purges its decrypted attachment cache).
    for (const cb of lockCallbacks) {
      try {
        cb();
      } catch {
        // A broken app callback should not block locking.
      }
    }
    // Keep the session authenticated; the user just needs to re-enter their
    // Master Code to resume editing. IdentityGate re-shows the unlock prompt
    // automatically once the vaultKey is null.
    set({
      privateKey: null,
      rawVaultKey: null,
      vaultKey: null,
      _lockTimer: null,
    });
  },

  async signOut() {
    // Run every registered sign-out prep so apps can flush durable state
    // before keys are zeroed. Each prep returns:
    //   - pendingCount: edits that COULD NOT be flushed
    //   - wipe: async callback that clears the app's IndexedDB
    // We confirm with the user if anything is still pending — otherwise
    // sign-out can leave ghost rows in IndexedDB that survive re-sign-in.
    const preps: SignOutPrepResult[] = [];
    for (const cb of signOutPrepCallbacks) {
      try {
        preps.push(await cb());
      } catch (err) {
        console.warn('[vault] signOut prep callback threw', err);
      }
    }
    const totalPending = preps.reduce((acc, p) => acc + p.pendingCount, 0);
    if (totalPending > 0) {
      const summary = preps
        .filter((p) => p.pendingCount > 0)
        .map((p) => p.description ?? `${p.pendingCount} unsynced edit(s)`)
        .join('; ');
      const ok = window.confirm(
        `You have ${summary}. Sign out anyway and discard them?`,
      );
      if (!ok) return; // stay signed in; user can retry the sync.
    }
    // User confirmed (or there was nothing pending) — wipe each app's
    // IndexedDB. This is the load-bearing step that prevents stale ghost
    // rows from surviving the sign-out / sign-in cycle.
    for (const p of preps) {
      try {
        await p.wipe();
      } catch (err) {
        console.warn('[vault] signOut wipe threw', err);
      }
    }
    // Best-effort: revoke the server-side trusted-device row and clear the
    // cookie. Fire-and-forget — local state is cleared regardless.
    void api.logout();
    get().lock();
    setAccessToken(null);
    set({
      status: 'signed_out',
      user: null,
      enrolment: null,
      encryptedPrivateKey: null,
      wrappedVaultKey: null,
      vaultExists: false,
      vaultRole: null,
      _lockTimer: null,
    });
  },

  touch() {
    const t = get()._lockTimer;
    if (t) clearTimeout(t);
    // Auto-lock is only meaningful when there's actually a key in memory.
    // Presence of `vaultKey` is the "Vault is unlocked" signal — when a
    // second crypto app lands, this becomes "any app key in memory".
    if (!get().vaultKey) return;
    const timer = setTimeout(() => get().lock(), get().autoLockMs);
    set({ _lockTimer: timer });
  },

  setAutoLockMs(ms: number) {
    set({ autoLockMs: ms });
    localStorage.setItem('dragonhq.autoLockMs', String(ms));
    get().touch();
  },
}));

export function installActivityListeners() {
  const touch = () => useVault.getState().touch();
  const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
  for (const ev of events) window.addEventListener(ev, touch, { passive: true });
  return () => {
    for (const ev of events) window.removeEventListener(ev, touch);
  };
}

/**
 * Register a callback to fire whenever the user locks (either manually or via
 * auto-lock). Apps with in-memory decrypted material (blob caches, BM25
 * indexes, etc.) use this to scrub their state. Returns an unsubscribe fn —
 * apps normally register once at module load and never unsubscribe, so the
 * return value is usually ignored.
 */
export function registerLockCallback(cb: () => void): () => void {
  lockCallbacks.add(cb);
  return () => lockCallbacks.delete(cb);
}

/**
 * Register a callback invoked during `signOut()`. Used by apps to:
 *   1. Flush durable state (live Y.Docs, outbox) before keys are zeroed.
 *   2. Report any edits that could not be flushed so the user is asked
 *      whether to discard them rather than silently losing data.
 *   3. Wipe the app's IndexedDB after the user confirms — this prevents
 *      stale `pendingSync:true` rows from surviving the sign-out/sign-in
 *      cycle.
 */
export function registerSignOutPrep(cb: SignOutPrep): () => void {
  signOutPrepCallbacks.add(cb);
  return () => signOutPrepCallbacks.delete(cb);
}

/**
 * Register a probe that reports the number of edits sitting in local
 * storage waiting to be sent to the server. Multiple providers are summed.
 * Called by the unlock screen so the tech sees "N unsynced edits — unlock
 * to resume sync" instead of a generic prompt.
 */
export function registerPendingCountProvider(
  cb: PendingCountProvider,
): () => void {
  pendingCountProviders.add(cb);
  return () => pendingCountProviders.delete(cb);
}

export async function getPendingCountTotal(): Promise<number> {
  let total = 0;
  for (const cb of pendingCountProviders) {
    try {
      total += await cb();
    } catch {
      /* ignore — best-effort signal, never block unlock */
    }
  }
  return total;
}
